import { create } from 'zustand';
import { authAPI } from '../services/api';

export const useAuthStore = create((set) => ({
  user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null,
  token: localStorage.getItem('token') || null,
  isAuthenticated: !!localStorage.getItem('token'),
  loading: false,
  error: null,

  register: async (token, password, email) => {
    set({ loading: true, error: null });
    try {
      await authAPI.register({ token, password, ...(email ? { email } : {}) });
      set({ loading: false });
      return true;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Registration failed', loading: false });
      return false;
    }
  },

  login: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const response = await authAPI.login(email, password);
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      set({
        user: response.user,
        token: response.token,
        isAuthenticated: true,
        loading: false,
      });
      return true;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Login failed', loading: false });
      return false;
    }
  },

  requestSmsToken: async (roll_no, phone) => {
    set({ loading: true, error: null });
    try {
      await authAPI.requestSmsToken(roll_no, phone);
      set({ loading: false });
      return true;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to send token', loading: false });
      return false;
    }
  },

  loginWithSmsToken: async (roll_no, token) => {
    set({ loading: true, error: null });
    try {
      const response = await authAPI.verifySmsToken(roll_no, token);
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      set({
        user: response.user,
        token: response.token,
        isAuthenticated: true,
        loading: false,
      });
      return true;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Invalid token', loading: false });
      return false;
    }
  },

  syncPermissions: (permissions) => {
    const current = useAuthStore.getState().user;
    if (!current) return;
    const next = permissions || [];
    const prev = current.permissions || [];
    // Only touch the store (and thus `user`'s object identity) when the set
    // actually changed. Sidebar re-fetches profile in a useEffect keyed on
    // `user` — writing a new object every call, even with identical content,
    // re-triggers that effect and creates an infinite fetch loop.
    const same = prev.length === next.length && prev.every((p) => next.includes(p));
    if (same) return;
    const updatedUser = { ...current, permissions: next };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    set({ user: updatedUser });
  },

  hasPermission: (key) => {
    const user = useAuthStore.getState().user;
    return !!user && (user.role === 'admin' || (user.permissions || []).includes(key));
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    set({
      user: null,
      token: null,
      isAuthenticated: false,
    });
  },

  updateProfile: async (data) => {
    set({ loading: true, error: null });
    try {
      await authAPI.updateProfile(data);
      const updatedUser = { ...useAuthStore.getState().user, ...data };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      set({ user: updatedUser, loading: false });
      return true;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Update failed', loading: false });
      return false;
    }
  },

  clearError: () => set({ error: null }),
}));
